import {
  FormEventHandler,
  MutableRefObject,
  ReactElement,
  MouseEvent as ReactMouseEvent,
  Ref,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { InputRef } from 'antd';
import classcat from 'classcat';
import { getType } from 'utils/betslip/betslip';
import { getScrollParent } from 'utils/bom-dom-manipulation/get-scroll-parent';
import { BETSLIP_STAKE_LIMIT, SEPARATOR } from 'utils/constants/betslip';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import {
  getValidPositiveNumber,
  thousandSeparator
} from 'utils/helpers/validators';
import { isMobile } from 'utils/is-mobile';
import { EDecimalRoundTypes } from 'utils/string-manipulation/round-multiple-value';
import { EBetslipBetType } from 'interfaces/betslip';
import { Max, Placeholder, StakeWrapper, Value } from './style';
import { EachWay } from 'newelements/_Betslip/components/EachWay';
import {
  roundAmount,
  roundOdd
} from 'newelements/_Betslip/utils/roundings/round-amount';
import { Input } from 'newcomponents/UI/Input';
import { useBalance } from 'hooks/useBalance';
import { useCurrency } from 'hooks/useCurrency';
import { useRefValue } from 'hooks/useRefValue';
import {
  getAllLsBets,
  getBetslipType,
  getEditBet,
  getSelectedSystem,
  getType as getTypeSelector
} from 'store/selectors/betslip';
import { getIsLoggedIn } from 'store/selectors/user-data';
import './index.less';

type Props = {
  stake: number | string;
  inputDefaultValue: number | string;
  active: boolean;
  eventIdForStakes?: string;
  max?: boolean;
  maxValue?: string;
  placeholder?: string;
  isLoading?: boolean;
  onClick?: (event: ReactMouseEvent<HTMLDivElement, MouseEvent>) => void;
  onChange?: (value: string, eventId?: string) => void;
  onMaxClick?: (event: ReactMouseEvent) => void;
  onPressEnter?: () => void;
  onFocus?: Function;
  changeCurrentStake?: Function;
  currentStake?: string | number;
  disabled?: boolean;
  isLast?: boolean;
  shouldUpdate?: string;
  refItem?: MutableRefObject<HTMLInputElement | null>;
  isOdd?: boolean;
  checkCurrencyContent?: boolean;
  dataTestIdPrefix?: string;
  inMultipleOfTheDay?: boolean;
  maxNotConvertedValue?: string;
};

const STAKE_MAX_LENGTHS = {
  withSeparator: 12,
  withoutSeparator: 10
};

const STAKE_MAX_LENGTH = SEPARATOR
  ? STAKE_MAX_LENGTHS.withSeparator
  : STAKE_MAX_LENGTHS.withoutSeparator;

export const StakeField = (props: Props): ReactElement => {
  const isLoggedIn = useSelector(getIsLoggedIn);
  const editBet = useSelector(getEditBet);
  const allLsBets = useSelector(getAllLsBets);
  const betslipType = useSelector(getBetslipType);
  const system = useSelector(getSelectedSystem);
  const { t } = useTranslation();
  const { currency, currencyId, placement } = useCurrency();

  const [focused, setFocused] = useState(false);
  const [ewAllowed, setEwAllowed] = useState(false);
  const inputRef =
    useRef<HTMLInputElement>() as MutableRefObject<HTMLInputElement>;

  const type = useSelector(getTypeSelector);
  const currentType = getType(type);

  const balance = useBalance();

  const currentTypeRefValue = useRefValue(currentType);

  const inputValue = props.stake ? props.stake.toString() : '';

  const changeStake = (value: string) => {
    props.onChange?.(value, props.eventIdForStakes);
    props?.changeCurrentStake?.(value);
  };

  useEffect(() => {
    if (props.changeCurrentStake && currentTypeRefValue !== 'single') {
      props.changeCurrentStake(
        props.stake ??
          (props.inputDefaultValue !== '0' ? props.inputDefaultValue : '')
      );
    }
  }, []);

  useEffect(() => {
    if (focused) {
      props.onFocus?.();
    }
  }, [focused]);

  useEffect(() => {
    if (betslipType === 0 && currentType !== 'single' && allLsBets.length > 1) {
      setEwAllowed(!allLsBets.find(bet => !bet.ewAllowed));
    } else {
      setEwAllowed(false);
    }
  }, [allLsBets, currentType, betslipType]);

  useLayoutEffect(() => {
    const { refItem } = props;
    const currentRef = refItem ?? inputRef;

    if (props.isLast && currentRef && currentRef?.current) {
      const scrollParent = getScrollParent(currentRef.current) as HTMLElement;

      scrollParent?.scrollTo(0, scrollParent.scrollHeight);
      currentRef.current.focus?.({ preventScroll: true });
    }
  }, [props.shouldUpdate]);

  const onInputHandler: FormEventHandler<HTMLInputElement> = ({ target }) => {
    const { value } = target as HTMLInputElement;

    const validPosNumber = getValidPositiveNumber(value, SEPARATOR) || '';

    const roundFn = props.isOdd
      ? (v: string) => roundOdd(v, EDecimalRoundTypes.Cut)
      : roundAmount;

    const formattedValue = roundFn(validPosNumber).toString();

    if (
      props.isOdd ||
      (formattedValue && formattedValue.length < STAKE_MAX_LENGTH)
    ) {
      changeStake(formattedValue);
    } else {
      changeStake('');
    }
  };

  const showCurrency: { left: boolean; right: boolean } = {
    left:
      props.checkCurrencyContent === false
        ? false
        : !!currencyId && placement === 'left' && !props.isOdd,
    right:
      props.checkCurrencyContent === false
        ? false
        : !!currencyId && placement === 'right' && !props.isOdd
  };

  const stakeInputValue =
    (thousandSeparator(props.currentStake || '') ||
      thousandSeparator(inputValue)) ??
    thousandSeparator(props.inputDefaultValue);

  const showCombinationCount =
    currentType === EBetslipBetType.SYSTEM &&
    !!system?.combinationCount &&
    !props.inMultipleOfTheDay;

  return (
    <div
      className={classcat([
        'stakeField--row',
        {
          'stakeField--row--left': showCurrency.left
        }
      ])}
      onClick={props.onClick}
    >
      {showCurrency.left && (
        <div
          className={classcat([
            'stakeField--currency',
            {
              'stakeField--currency--focused':
                focused || (isMobile() && props.active)
            }
          ])}
        >
          {currency}
        </div>
      )}
      <StakeWrapper
        $paddingLeft={showCurrency.left}
        data-testid={`${props.dataTestIdPrefix || 'stake-'}input`}
      >
        {isMobile() ? (
          <Value
            className="betslip__placeholder"
            active={props.active && !props.disabled}
          >
            {stakeInputValue || (
              <Placeholder $paddingLeft={showCurrency.left}>
                {(!isMobile() && editBet?.amount) ||
                  (props.active ? '' : props.placeholder)}
              </Placeholder>
            )}
          </Value>
        ) : (
          <Input
            className={classcat([
              'stakeField--antdInput',
              {
                'stakeField--antdInput--left': showCurrency.left
              }
            ])}
            style={{
              width: 'calc(100% - 30px)',
              ...(props.maxValue && { marginLeft: -11 })
            }}
            bordered={false}
            value={stakeInputValue}
            ref={(props.refItem ?? inputRef) as Ref<InputRef>}
            placeholder={
              (editBet?.active && t('betslip.editBetPlaceholder')) ||
              props.placeholder
            }
            maxLength={
              SEPARATOR ? BETSLIP_STAKE_LIMIT + 2 : BETSLIP_STAKE_LIMIT
            }
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onInput={onInputHandler}
            onPressEnter={props.onPressEnter}
            disabled={props.disabled}
          />
        )}
      </StakeWrapper>
      {showCombinationCount && (
        <div
          className={isMobile() ? 'combinationCountMob' : 'combinationCount'}
        >
          (x{system.combinationCount})
        </div>
      )}
      {showCurrency.right && (
        <div
          className={classcat([
            'stakeField--currency',
            {
              'stakeField--currency--focused':
                focused || (isMobile() && props.active)
            },
            { 'stakeField--currency--left': isMobile() && showCurrency.left }
          ])}
        >
          {currency}
        </div>
      )}
      {isLoggedIn &&
      (props.max || editBet?.active) &&
      (SpringConfigs.BETSLIP_MAX_BTN || !!props.maxValue) ? (
        <div className="stakeField--maxWrapper">
          <Max
            type="text"
            size="middle"
            data-testid={`${props.dataTestIdPrefix || ''}max-button`}
            loading={props.isLoading}
            onClick={props.onMaxClick}
            $oddOffer={!!props.maxValue}
            disabled={
              props.disabled ||
              !!(SpringConfigs.BETSLIP_MAX_BTN_TYPE && !balance)
            }
            icon={
              <>
                <span>
                  {t(
                    `account.${
                      !SpringConfigs.BETSLIP_MAX_BTN_TYPE ? 'max' : 'allIn'
                    }`
                  )}
                </span>
                {!!props.maxValue && (
                  <div>
                    &nbsp;{props.maxValue}{' '}
                    {props.maxNotConvertedValue &&
                      `(${props.maxNotConvertedValue})`}
                  </div>
                )}
              </>
            }
          />
        </div>
      ) : null}
      {ewAllowed && <EachWay type={2} />}
    </div>
  );
};
